import doNothing from '@/helpers/api/doNothing.js'
import handleApiError from '@/helpers/logging/handleApiError.js'
import axiosHttpClient from '@/helpers/axiosHttpClient.js'
import buildQueryString from '@/helpers/urls/buildQueryString.js'
import Logger from '@/helpers/logging/Logger.js'

export default class BaseCollection {
	/** @type {string} */
	url = ''
	/** @type {object[]} */
	items = []
	/** @type {object} */
	links = {}
	/** @type {object} */
	meta = {
		current_page: 1,
		last_page: 1,
		per_page: 10,
		total: null
	}
	/** @type {boolean} */
	_loading = false

	_filters = []
	_page = 1
	_amount = 10
	_query = ''
	_search = ''
	_sorting  = { field: null, order: null }

	constructor() {}

	get loading() { return this._loading }

	set page(page) {
		this._page = page
		this.refetch()
	}

	set amount(amount) {
		this._amount = amount
		this.refetch()
	}

	/**
	 * @typedef {object} SortOptions
	 * @property {string|null} field - The field to sort by
	 * @property {string|null} order - Whether to sort ascending or descending for the given field
	 */
	/**
	 * @param {object} options - A configuration object
	 * @param {Array} [options.filters] - Which filters to apply
	 * @param {string} [options.query] - The query to apply
	 * @param {string} [options.search] - The search terms to look for
	 * @param {number} [options.currentPage] - The page to start results at
	 * @param {number} [options.perPage] - The number of items per page
	 * @param {SortOptions} [options.sorting] - Which sorting to apply
	 * @param {AbortSignal} [options.signal] - An abort signal to stop the function
	 * @returns {Promise} - The fetch request
	 */
	async fetchByQuery({
		filters = [],
		query = '',
		search = '',
		currentPage = this._page,
		perPage = this._amount,
		sorting = { field: null, order: null },
		signal = null
	} = {})
	{
		this._filters = filters
		this._query = query
		this._search = search
		this._page = currentPage
		this._amount = perPage
		this._sorting = sorting
		this._signal = signal

		const url = buildQueryString(
			this.url,
			{
				currentPage,
				filters,
				perPage,
				query,
				search,
				sorting
			}
		)
		this._loading = true
		this.meta ??= {}
		this.meta.total = null
		return await axiosHttpClient
			.get(url, { signal })
			.then(result => {
				if ((result?.data?.meta?.last_page ?? 1) < this._page) {
					Logger.debug(`
						currentPage: ${this._page}
						last_page: ${result?.data?.meta?.last_page ?? 1}
					`)
					this.page = 1
					return
				}
				const items = result?.data?.data ?? []
				this.items = this.formatData(items)
				this.meta = result?.data?.meta ?? {}
				this.links = result?.data?.links ?? {}
			})
			.catch(handleApiError)
			.finally(() => { this._loading = false })
	}

	refetch() {
		this.fetchByQuery({
			filters: this._filters,
			query: this._query,
			search: this._search,
			currentPage: this._page,
			perPage: this._amount,
			sorting: this._sorting,
			signal: this._signal
		}).then(doNothing)
	}

	/**
	 * @param {AbortSignal} [signal] - An abort signal to stop the fetch
	 * @returns {Promise} - The fetch request
	 */
	async fetchAll(signal) {
		return this.fetchByQuery({
			perPage: 99_999,
			signal
		})
	}

	/**
	 * @param {object} [options] - A configuration object
	 * @param {Array} [options.filters] - Filters for the endpoint
	 * @param {AbortSignal} [options.signal] - The AbortSignal for this request
	 * @param {boolean} [options.subData] - Whether to check `response.data.data` instead of `response.data`
	 * @param {Function} [options.callback] - A callback to format the data before setting loading to false
	 * @returns {Promise} - The fetch request
	 */
	async fetchDropdown({
		filters = [],
		signal= undefined,
		subData = false,
		callback = undefined
	} = {}) {
		const url = buildQueryString(this.url + '/dropdown', {
			currentPage: null,
			perPage: null,
			filters,
		})
		this._loading = true
		return await axiosHttpClient
			.get(url, { signal })
			.then(response => {
				let data = subData ? response?.data?.data : response?.data
				if (callback !== undefined) {
					data = callback(data)
				}
				this.items = data ?? []
			})
			.catch(handleApiError)
			.finally(() => { this._loading = false })
	}

	/**
	 * Handle bulk actions by taking an `action` and a list of `ids`
	 * @param {string} action The bulk action to call
	 * @param {number[]} ids The IDs to call the bulk action on
	 * @returns {Promise} A delete or patch promise
	 */
	bulk(action = '', ids = []) {
		this._loading = true
		if (action === 'delete') {
			return axiosHttpClient
				.delete(this.url + '/' + 'delete', { data: { ids } })
				.finally(() => { this._loading = false })
		} else {
			return axiosHttpClient
				.patch(this.url + '/' + action, { ids })
				.finally(() => { this._loading = false })
		}
	}

	/**
	 * Handle bulk actions statically by taking an `action` and a list of `ids`
	 * @param {string} action The bulk action to call
	 * @param {number[]} ids The IDs to call the bulk action on
	 * @returns {Promise} A delete or patch promise
	 */
	static bulk(action = '', ids = []) {
		const url = new this().url
		if (action === 'delete') {
			return axiosHttpClient.delete(url + '/' + 'delete', { data: { ids } })
		} else {
			return axiosHttpClient.patch(url + '/' + action, { ids })
		}
	}

	/**
	 * @param {Array} rows - The rows to format
	 * @returns {Array} - The formatted data
	 */
	formatData(rows) {
		return rows
	}

	/**
	 * @returns {Array} the table config for a SuperTable
	 */
	static tableConfig() {
		return []
	}

	get tableConfig() {
		return this?.constructor?.tableConfig?.() ?? []
	}
}
